import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock } from "lucide-react";
import { toast } from "sonner";

const rp = (n) => "Rp" + (n == null ? "0" : Math.round(n).toLocaleString("id-ID"));

const EMPTY_EDIT = {
  asset_code: "", name: "", category: "", in_service_date: "", acquisition_cost: "",
  residual_value: "0", useful_life: "", location: "", notes: "", reason: "", rev: 0,
};

export default function AssetRegister() {
  const { can } = useAuth();
  const canManage = can("accounting.journal.manage");
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [f, setF] = useState({ asset_code: "", name: "", category: "", in_service_date: "", acquisition_cost: "", residual_value: "0", useful_life: "" });

  // --- Edit modal state ---
  const [editing, setEditing] = useState(null); // the asset being edited (holds lock flags)
  const [ef, setEf] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setAssets((await api.get("/finance/assets")).data);
      setSummary((await api.get("/finance/assets-report/summary")).data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/finance/assets", {
        ...f, acquisition_cost: Number(f.acquisition_cost), residual_value: Number(f.residual_value || 0),
        useful_life: Number(f.useful_life),
      });
      toast.success("Aset dibuat (Draft)"); setShowForm(false);
      setF({ asset_code: "", name: "", category: "", in_service_date: "", acquisition_cost: "", residual_value: "0", useful_life: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const activate = async (id) => { try { await api.post(`/finance/assets/${id}/activate`); toast.success("Aset aktif"); load(); } catch (e) { toast.error(apiError(e)); } };
  const depreciate = async (id) => {
    const period = prompt("Periode penyusutan (YYYY-MM):");
    if (!period) return;
    try { const r = await api.post(`/finance/assets/${id}/depreciate`, { period }); toast.success(`Jurnal penyusutan Draft ${r.data.journal_no} · ${rp(r.data.amount)}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const viewSchedule = async (id) => { try { setSchedule((await api.get(`/finance/assets/${id}/schedule`)).data); } catch (e) { toast.error(apiError(e)); } };

  const openEdit = (a) => {
    setEditing(a);
    setEf({
      asset_code: a.asset_code || "", name: a.name || "", category: a.category || "",
      in_service_date: a.in_service_date || "", acquisition_cost: String(a.acquisition_cost ?? ""),
      residual_value: String(a.residual_value ?? "0"), useful_life: String(a.useful_life ?? ""),
      location: a.location || "", notes: a.notes || "", reason: "", rev: a.rev ?? 0,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    if ((ef.reason || "").trim().length < 3) { toast.error("Alasan perubahan wajib diisi (minimal 3 karakter)."); return; }
    setSaving(true);
    try {
      await api.patch(`/finance/assets/${editing.id}`, {
        reason: ef.reason.trim(), rev: ef.rev,
        asset_code: ef.asset_code, name: ef.name, category: ef.category,
        in_service_date: ef.in_service_date,
        acquisition_cost: Number(ef.acquisition_cost), residual_value: Number(ef.residual_value || 0),
        useful_life: Number(ef.useful_life),
        location: ef.location, notes: ef.notes,
      });
      toast.success("Perubahan aset tersimpan");
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const locked = !!editing?.financial_locked;

  return (
    <div className="p-6" data-testid="asset-register-page">
      <div className="flex justify-between items-center mb-4">
        <div><h1 className="text-2xl font-semibold text-white">Aset Tetap</h1>
          <p className="text-slate-400 text-sm">Register aset & penyusutan garis lurus. Jurnal penyusutan dibuat DRAFT (diposting Accounting).</p></div>
        {canManage && <Button data-testid="new-asset-btn" onClick={() => setShowForm(!showForm)} className="bg-indigo-600 hover:bg-indigo-500">+ Aset Baru</Button>}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Card className="p-4 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Jumlah Aset</div><div className="text-xl font-semibold text-white">{summary.count}</div></Card>
          <Card className="p-4 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Nilai Perolehan</div><div className="text-xl font-semibold text-white">{rp(summary.total_acquisition)}</div></Card>
          <Card className="p-4 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Akumulasi Penyusutan</div><div className="text-xl font-semibold text-amber-400">{rp(summary.total_accumulated)}</div></Card>
          <Card className="p-4 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Nilai Buku</div><div className="text-xl font-semibold text-emerald-400">{rp(summary.total_book_value)}</div></Card>
        </div>
      )}

      {showForm && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs text-slate-400">Kode Aset</label><Input data-testid="asset-code" value={f.asset_code} onChange={(e) => setF({ ...f, asset_code: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Nama Aset</label><Input data-testid="asset-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Kategori</label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Tgl Mulai Digunakan</label><Input data-testid="asset-inservice" type="date" value={f.in_service_date} onChange={(e) => setF({ ...f, in_service_date: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Nilai Perolehan</label><Input data-testid="asset-cost" type="number" value={f.acquisition_cost} onChange={(e) => setF({ ...f, acquisition_cost: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Nilai Residu</label><Input type="number" value={f.residual_value} onChange={(e) => setF({ ...f, residual_value: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Umur Manfaat (bulan)</label><Input data-testid="asset-life" type="number" value={f.useful_life} onChange={(e) => setF({ ...f, useful_life: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
          </div>
          <div className="mt-3"><Button data-testid="save-asset" onClick={create} className="bg-emerald-600 hover:bg-emerald-500">Simpan Aset</Button></div>
        </Card>
      )}

      <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
        <table className="w-full text-sm text-slate-300">
          <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">Kode</th><th>Nama</th><th>Perolehan</th><th>Ak. Penyusutan</th><th>Nilai Buku</th><th>Penyusutan/bln</th><th>Status</th><th>Aksi</th></tr></thead>
          <tbody>
            {assets.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-500">Belum ada data.</td></tr>}
            {assets.map((a) => (
              <tr key={a.id} data-testid={`asset-row-${a.asset_code}`} className="border-b border-slate-800">
                <td className="p-3">{a.asset_code}</td><td>{a.name}</td><td>{rp(a.acquisition_cost)}</td>
                <td>{rp(a.accumulated_depreciation)}</td><td>{rp(a.book_value)}</td><td>{rp(a.monthly_depreciation)}</td>
                <td><span className="text-xs px-2 py-1 rounded bg-slate-700/40">{a.status}</span></td>
                <td className="space-x-1 whitespace-nowrap">
                  {canManage && <Button data-testid={`edit-${a.asset_code}`} onClick={() => openEdit(a)} className="h-7 px-2 bg-slate-700 hover:bg-slate-600 text-xs">Edit</Button>}
                  {canManage && a.status === "Draft" && <Button data-testid={`activate-${a.asset_code}`} onClick={() => activate(a.id)} className="h-7 px-2 bg-indigo-600 hover:bg-indigo-500 text-xs">Aktifkan</Button>}
                  {canManage && a.status === "Aktif" && <Button data-testid={`depreciate-${a.asset_code}`} onClick={() => depreciate(a.id)} className="h-7 px-2 bg-amber-600 hover:bg-amber-500 text-xs">Susutkan</Button>}
                  <Button data-testid={`schedule-${a.asset_code}`} onClick={() => viewSchedule(a.id)} className="h-7 px-2 bg-slate-700 hover:bg-slate-600 text-xs">Jadwal</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {schedule && (
        <Card className="p-4 bg-slate-900 border-slate-800 mt-4" data-testid="depreciation-schedule">
          <div className="flex justify-between mb-2"><h3 className="text-white font-semibold">Jadwal Penyusutan — {schedule.asset.asset_code} ({rp(schedule.monthly_depreciation)}/bln)</h3>
            <button onClick={() => setSchedule(null)} className="text-slate-400 text-sm">Tutup</button></div>
          <table className="w-full text-sm text-slate-300"><thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="py-2">Periode</th><th>Awal</th><th>Penyusutan</th><th>Akumulasi</th><th>Akhir</th><th>Jurnal</th></tr></thead>
            <tbody>{schedule.schedule.map((s) => <tr key={s.period} className="border-b border-slate-800"><td className="py-2">{s.period}</td><td>{rp(s.opening_book_value)}</td><td>{rp(s.depreciation)}</td><td>{rp(s.accumulated)}</td><td>{rp(s.closing_book_value)}</td><td className="text-xs">{s.journal_status}</td></tr>)}</tbody></table>
        </Card>
      )}

      {/* ---------- Edit Asset Modal ---------- */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !saving) setEditing(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl" data-testid="edit-asset-modal">
          <DialogHeader><DialogTitle>Edit Aset Tetap {editing ? `— ${editing.asset_code}` : ""}</DialogTitle></DialogHeader>

          {locked && (
            <div data-testid="edit-lock-banner" className="flex items-start gap-2 rounded-md border border-amber-700/50 bg-amber-900/20 p-3 text-xs text-amber-300">
              <Lock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{editing?.lock_reason}</span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400">Kode Aset {locked && <Lock className="inline h-3 w-3 text-amber-400" />}</label>
              <Input data-testid="edit-asset-code" disabled={locked} value={ef.asset_code} onChange={(e) => setEf({ ...ef, asset_code: e.target.value })} className="bg-slate-800 border-slate-700 text-white disabled:opacity-50" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Nama Aset</label>
              <Input data-testid="edit-asset-name" value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Kategori</label>
              <Input data-testid="edit-asset-category" value={ef.category} onChange={(e) => setEf({ ...ef, category: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Tgl Mulai Digunakan {locked && <Lock className="inline h-3 w-3 text-amber-400" />}</label>
              <Input data-testid="edit-asset-inservice" type="date" disabled={locked} value={ef.in_service_date} onChange={(e) => setEf({ ...ef, in_service_date: e.target.value })} className="bg-slate-800 border-slate-700 text-white disabled:opacity-50" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Nilai Perolehan {locked && <Lock className="inline h-3 w-3 text-amber-400" />}</label>
              <Input data-testid="edit-asset-cost" type="number" disabled={locked} value={ef.acquisition_cost} onChange={(e) => setEf({ ...ef, acquisition_cost: e.target.value })} className="bg-slate-800 border-slate-700 text-white disabled:opacity-50" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Nilai Residu {locked && <Lock className="inline h-3 w-3 text-amber-400" />}</label>
              <Input data-testid="edit-asset-residual" type="number" disabled={locked} value={ef.residual_value} onChange={(e) => setEf({ ...ef, residual_value: e.target.value })} className="bg-slate-800 border-slate-700 text-white disabled:opacity-50" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Umur Manfaat (bulan) {locked && <Lock className="inline h-3 w-3 text-amber-400" />}</label>
              <Input data-testid="edit-asset-life" type="number" disabled={locked} value={ef.useful_life} onChange={(e) => setEf({ ...ef, useful_life: e.target.value })} className="bg-slate-800 border-slate-700 text-white disabled:opacity-50" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Lokasi</label>
              <Input data-testid="edit-asset-location" value={ef.location} onChange={(e) => setEf({ ...ef, location: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="text-xs text-slate-400">Catatan</label>
              <Textarea data-testid="edit-asset-notes" rows={2} value={ef.notes} onChange={(e) => setEf({ ...ef, notes: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="text-xs text-slate-400">Alasan Perubahan <span className="text-rose-400">*</span></label>
              <Textarea data-testid="edit-asset-reason" rows={2} value={ef.reason} onChange={(e) => setEf({ ...ef, reason: e.target.value })} placeholder="Wajib diisi — tersimpan di audit trail" className="bg-slate-800 border-slate-700 text-white" />
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="edit-cancel" disabled={saving} onClick={() => setEditing(null)} className="bg-slate-700 hover:bg-slate-600">Batal</Button>
            <Button data-testid="edit-save" disabled={saving} onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-500">{saving ? "Menyimpan…" : "Simpan Perubahan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
